import React from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@epoch-ui/components/DropdownMenu/dropdown-menu';
import { Button } from '@epoch-ui/components/Button';
import { Icon } from '@epoch-ui/components/Icon';

import {
  EPOCH_COMMUNICATION_STATUS,
  EPOCH_COMMUNICATION_TYPE,
  EPOCH_COMMUNICATION_NOTIFICATION
} from '../../types/communication.types';

interface NotificationCardDropdownProps {
  epochCommunication: EPOCH_COMMUNICATION_NOTIFICATION;
  onEdit: () => void;
  onPreview: () => void;
  onDelete?: () => void;
}

/**
 * Responsible for dropdown menu for each notification card.
 * - Preview: Preview the notification message.
 * - Edit: Edit the notification.
 * - Delete: Delete the notification (only for RESEND_FEEDBACK).
 *
 * Conditions:
 * - If status is SENT: show eye icon only (preview)
 * - If status is SCHEDULED: show 3-dot dropdown with preview, edit, and delete (for RESEND_FEEDBACK)
 */
export const NotificationCardDropdown: React.FC<NotificationCardDropdownProps> = ({
  epochCommunication,
  onEdit,
  onPreview,
  onDelete
}) => {
  const { type, status } = epochCommunication;

  // If status is not scheduled, only show preview button (eye icon)
  if (status !== EPOCH_COMMUNICATION_STATUS.SCHEDULED) {
    return (
      <Button onClick={onPreview} variant="ghost" className="tw-h-6 tw-w-6 tw-p-0">
        <Icon name="eye" size={16} />
      </Button>
    );
  }

  // Determine which menu items to show based on type
  const showDelete = type === EPOCH_COMMUNICATION_TYPE.RESEND_FEEDBACK;
  const showDropdown =
    type === EPOCH_COMMUNICATION_TYPE.SELF_CHECK_IN ||
    type === EPOCH_COMMUNICATION_TYPE.FEEDBACK ||
    type === EPOCH_COMMUNICATION_TYPE.RESEND_FEEDBACK;

  if (!showDropdown) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="tw-h-6 tw-w-6 tw-p-0">
          <Icon name="ellipsis-vertical" size={16} className="tw-text-medium-grey" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="tw-w-32">
        <DropdownMenuItem>
          <Button onClick={onPreview} variant="ghost" className="tw-h-4 tw-p-0 tw-text-xs">
            <Icon name="eye" size={16} className="tw-mr-2" />
            Preview
          </Button>
        </DropdownMenuItem>

        <DropdownMenuItem>
          <Button onClick={onEdit} variant="ghost" className="tw-h-4 tw-p-0 tw-text-xs">
            <Icon name="pencil" size={16} className="tw-mr-2" />
            Edit
          </Button>
        </DropdownMenuItem>

        {showDelete && onDelete && (
          <DropdownMenuItem>
            <Button
              onClick={onDelete}
              variant="ghost"
              className="tw-h-4 tw-p-0 tw-text-xs tw-text-negative-foreground"
            >
              <Icon name="trash" size={16} className="tw-mr-2" />
              Delete
            </Button>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
